import { cva, type VariantProps } from "cva";

/**
 * Button Component Styles
 *
 * Structure: Variants → Contexts → Brands
 *
 * Variants:
 * - primary: Main action button with brand colors
 * - secondary: Secondary action with transparent background and border
 * - media: Special variant for media-related actions
 *
 * Contexts (only for primary/secondary):
 * - default: Standard background (white/light)
 * - onAccentDark: Dark accent background (brand primary colors)
 * - onAccentLight: Light accent background (brand background colors)
 *
 * Brands:
 * - ClassDojo (default): Purple color scheme
 * - Tutor: Emerald/green color scheme with distinctive borders
 * - Sparks: Blue color scheme (placeholder)
 * - Islands: Amber color scheme (placeholder)
 */
export const buttonStyles = cva({
  base: [
    "cursor-pointer flex items-center justify-center gap-x-4 font-ginto font-medium leading-100 transition-[background-color,transform,box-shadow] duration-220 ease-out-quad [&>svg]:fill-current",
    "tutor:transition-all tutor:duration-100 tutor:ease tutor:will-change-transform",
  ],
  variants: {
    variant: {
      primary: "border-1",
      secondary: "border-1",
      media: "border-1 backdrop-blur-button",
    },
    context: {
      default: "",
      onAccentDark: "",
      onAccentLight: "",
    },
    size: {
      xs: "px-12 text-ginto-14 rounded-xs h-32",
      s: "px-16 text-ginto-14 rounded-sm h-40",
      m: "px-24 text-ginto-16 rounded-default h-48",
      l: "px-32 text-ginto-18 rounded-md h-56",
    },
    disabled: {
      true: "cursor-not-allowed",
      false: "",
    },
  },
  compoundVariants: [
    // Focus states for primary and secondary buttons
    {
      variant: ["primary", "secondary"],
      context: "default",
      disabled: false,
      class: "outline-border focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-border",
    },
    {
      variant: ["primary", "secondary"],
      context: "onAccentDark",
      disabled: false,
      class: "outline-border focus-visible:outline-4 focus-visible:outline-offset-0 focus-visible:outline-chalk-600",
    },
    {
      variant: ["primary", "secondary"],
      context: "onAccentLight",
      disabled: false,
      class: "outline-border focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-border",
    },
    {
      variant: "media",
      context: ["default", "onAccentDark", "onAccentLight"],
      disabled: false,
      class: "outline-border focus-visible:outline-4 focus-visible:outline-offset-0 focus-visible:outline-ink-600",
    },

    // Primary Button Styles
    // Default Context
    {
      variant: "primary",
      disabled: false,
      context: "default",
      class: [
        "bg-brand-primary text-content-light border-transparent hover:bg-brand-primary-hover active:bg-brand-primary active:scale-98",
        "tutor:bg-emerald-300 tutor:text-brand-secondary tutor:border-2 tutor:border-emerald-950 tutor:shadow-[var(--shadow-button-hard-tutor)] tutor:hover:bg-emerald-400 tutor:active:bg-emerald-300 tutor:active:scale-100 tutor:active:shadow-none tutor:active:translate-x-4 tutor:active:translate-y-4",
        "sparks:bg-blue-700 sparks:text-content-light sparks:border-transparent sparks:hover:bg-blue-800 sparks:active:bg-blue-700",
        "islands:bg-amber-300 islands:text-content-primary islands:border-transparent islands:hover:bg-amber-400 islands:active:bg-amber-300",
      ],
    },
    // On Accent Dark Context
    {
      variant: "primary",
      disabled: false,
      context: "onAccentDark",
      class: [
        "bg-surface text-content-primary border-transparent hover:bg-chalk-800 active:bg-surface active:scale-98",
        "tutor:bg-surface tutor:text-content-primary tutor:border-transparent tutor:shadow-[var(--shadow-button-hard-tutor-on-dark)] tutor:hover:bg-chalk-800 tutor:active:bg-surface tutor:active:scale-100 tutor:active:shadow-none tutor:active:translate-x-4 tutor:active:translate-y-4",
        "sparks:bg-surface sparks:text-content-primary sparks:border-transparent sparks:hover:bg-chalk-800 sparks:active:bg-surface",
        "islands:bg-surface islands:text-content-primary islands:border-transparent islands:hover:bg-chalk-800 islands:active:bg-surface",
      ],
    },
    // On Accent Light Context
    {
      variant: "primary",
      disabled: false,
      context: "onAccentLight",
      class: [
        "bg-brand-primary text-content-light border-transparent hover:bg-brand-primary-hover active:bg-brand-primary active:scale-98",
        "tutor:bg-white tutor:text-brand-secondary tutor:border-2 tutor:border-emerald-950 tutor:shadow-[var(--shadow-button-hard-tutor)] tutor:hover:bg-chalk-800 tutor:active:bg-white tutor:active:scale-100 tutor:active:shadow-none tutor:active:translate-x-4 tutor:active:translate-y-4",
        "sparks:bg-blue-700 sparks:text-content-light sparks:border-transparent sparks:hover:bg-blue-800 sparks:active:bg-blue-700",
        "islands:bg-amber-300 islands:text-content-primary islands:border-transparent islands:hover:bg-amber-400 islands:active:bg-amber-300",
      ],
    },

    // Secondary Button Styles
    // Default Context
    {
      variant: "secondary",
      context: "default",
      disabled: false,
      class: [
        "bg-transparent text-content-primary dark:text-content-light border-border-decorative dark:bg-chalk-200 dark:hover:bg-chalk-300 dark:active:bg-chalk-300 hover:bg-ink-100 dark:hover:bg-chalk-200 active:bg-transparent active:scale-98 dark:active:bg-chalk-200",
        "tutor:bg-transparent tutor:text-content-primary tutor:border-2 tutor:border-emerald-950 tutor:shadow-[var(--shadow-button-hard-tutor)] tutor:hover:bg-ink-100 tutor:active:bg-transparent tutor:active:scale-100 tutor:active:shadow-none tutor:active:translate-x-4 tutor:active:translate-y-4",
        "sparks:bg-transparent sparks:text-content-primary sparks:border-border-decorative sparks:hover:bg-ink-100 sparks:active:bg-transparent",
        "islands:bg-transparent islands:text-content-primary islands:border-border-decorative islands:hover:bg-ink-100 islands:active:bg-transparent",
      ],
    },
    // On Accent Dark Context
    {
      variant: "secondary",
      context: "onAccentDark",
      disabled: false,
      class: [
        "bg-transparent text-content-light border-2 border-content-light hover:bg-ink-200 active:bg-transparent active:scale-98",
        "tutor:bg-transparent tutor:text-content-light tutor:border-2 tutor:border-content-light tutor:shadow-[var(--shadow-button-hard-tutor-on-dark)] tutor:hover:bg-ink-200 tutor:active:bg-transparent tutor:active:scale-100 tutor:active:shadow-none tutor:active:translate-x-4 tutor:active:translate-y-4",
        "sparks:bg-transparent sparks:text-content-light sparks:border-2 sparks:border-content-light sparks:hover:bg-ink-200 sparks:active:bg-transparent",
        "islands:bg-transparent islands:text-content-light islands:border-2 islands:border-content-light islands:hover:bg-ink-200 islands:active:bg-transparent",
      ],
    },
    // On Accent Light Context
    {
      variant: "secondary",
      context: "onAccentLight",
      disabled: false,
      class: [
        "bg-transparent text-content-primary border-border-decorative hover:bg-ink-100 active:bg-transparent active:scale-98",
        "tutor:bg-transparent tutor:text-content-primary tutor:border-2 tutor:border-content-primary tutor:shadow-[var(--shadow-button-hard-tutor)] tutor:hover:bg-ink-100 tutor:active:bg-transparent tutor:active:scale-100 tutor:active:shadow-none tutor:active:translate-x-4 tutor:active:translate-y-4",
        "sparks:bg-transparent sparks:text-content-primary sparks:border-border-decorative sparks:hover:bg-ink-100 sparks:active:bg-transparent",
        "islands:bg-transparent islands:text-content-primary islands:border-border-decorative islands:hover:bg-ink-100 islands:active:bg-transparent",
      ],
    },

    // Media Button Styles (no context variants)
    {
      variant: "media",
      disabled: false,
      class: "bg-ink-500 text-white border-transparent hover:bg-ink-600 active:bg-ink-500 active:scale-98",
    },

    // Disabled States
    {
      variant: "primary",
      disabled: true,
      class: "bg-ink-200 text-ink-400 border-transparent",
    },
    {
      variant: "secondary",
      disabled: true,
      class: "bg-ink-200 text-ink-400 border-transparent",
    },
    {
      variant: "media",
      disabled: true,
      class: "bg-ink-200 text-ink-400 border-transparent",
    },

    // Icon Sizing
    {
      size: ["xs", "s"],
      class: "[&>svg]:w-18 [&>svg]:h-18",
    },
    {
      size: ["m", "l"],
      class: "[&>svg]:w-24 [&>svg]:h-24",
    },
  ],
});

export type ButtonStylesProps = VariantProps<typeof buttonStyles>;
